import { useCommitContractImport, useGetAllStudents, usePreviewContractImport } from '@actions';
import Button from '@components/V4/Button';
import Input from '@components/V4/Inputs/Input';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import Table from '@components/V4/Table/Table';
import { useDebounce } from '@hooks';
import {
  ContractImportCommitRowDTO,
  ContractImportResultDTO,
  ContractImportRowDTO,
} from '@shared/clientTypes/DTOs/ContractsDTO';
import { ContractPaymentType } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { useMemo, useRef, useState } from 'react';
import { formatDate, formatMoney } from '../utils';

interface ContractImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenContract: (contractId: string) => void;
}

type DraftRow = ContractImportRowDTO & {
  studentId?: string;
  studentLabel?: string;
  sessions: string;
  installments: string;
  include: boolean;
};

type Step = 'pick' | 'review' | 'done';

const PAYMENT_LABELS: Record<ContractPaymentType, string> = {
  [ContractPaymentType.Full]: 'Paid in full',
  [ContractPaymentType.Installments]: 'Installments',
};

const toDraft = (row: ContractImportRowDTO): DraftRow => ({
  ...row,
  studentId: row.match?._id,
  studentLabel: row.match?.fullName,
  sessions: '',
  installments: String(row.installmentsCount),
  include: !row.errors.length && !row.existingContract,
});

const readFileText = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file, 'utf-8');
  });

const StudentPicker = (props: { row: DraftRow; onPick: (id: string, label: string) => void }) => {
  const { row, onPick } = props;
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 400);
  const { data: students, isLoading } = useGetAllStudents(
    { search: debounced, limit: 20 },
    { enabled: debounced.trim().length >= 2 }
  );

  const options = useMemo(() => {
    const seen = new Set<string>();
    const list: { label: string; value: string }[] = [];
    const push = (id?: string, label?: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      list.push({ value: id, label: label ?? id });
    };
    push(row.studentId, row.studentLabel);
    row.candidates.forEach((candidate) => push(candidate._id, `${candidate.fullName} (candidate)`));
    (students ?? []).forEach((student) => push(student._id, student.fullName));
    return list;
  }, [row.studentId, row.studentLabel, row.candidates, students]);

  return (
    <Select
      showSearch
      allowClear={false}
      filterOption={() => true}
      className="min-w-48"
      placeholder={row.candidates.length ? 'Pick a candidate' : 'Search student'}
      options={options}
      isLoading={isLoading}
      value={row.studentId}
      onSearch={setSearch}
      onSelect={(value) => {
        const option = options.find((item) => item.value === String(value));
        onPick(String(value), option?.label.replace(/ \(candidate\)$/, '') ?? String(value));
      }}
    />
  );
};

const ContractImportModal = (props: ContractImportModalProps) => {
  const { isOpen, onClose, onOpenContract } = props;
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('pick');
  const [fileName, setFileName] = useState<string>();
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [result, setResult] = useState<ContractImportResultDTO>();

  const { mutate: preview, isLoading: isPreviewing } = usePreviewContractImport({
    onSuccess: (parsed) => {
      setRows(parsed.map(toDraft));
      setStep('review');
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'The file could not be read.'),
  });

  const { mutate: commit, isLoading: isCommitting } = useCommitContractImport({
    onSuccess: (outcome) => {
      setResult(outcome);
      setStep('done');
      toast.success(
        `${outcome.created} contract${outcome.created === 1 ? '' : 's'} created${
          outcome.skipped ? `, ${outcome.skipped} skipped` : ''
        }${outcome.failed ? `, ${outcome.failed} failed` : ''}.`
      );
    },
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'The import failed.'),
  });

  const reset = () => {
    setStep('pick');
    setFileName(undefined);
    setRows([]);
    setResult(undefined);
    if (fileInput.current) fileInput.current.value = '';
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const csv = await readFileText(file);
      preview({ csv });
    } catch {
      toast.error('The file could not be read.');
    }
  };

  const updateRow = (index: number, patch: Partial<DraftRow>) =>
    setRows((current) => current.map((row) => (row.index === index ? { ...row, ...patch } : row)));

  const isRowReady = (row: DraftRow) =>
    !!row.studentId && !!row.amount && !!row.contractDate && !row.existingContract;

  const selected = rows.filter((row) => row.include && isRowReady(row));
  const blocked = rows.filter((row) => row.include && !isRowReady(row));

  const submit = () => {
    const payload: ContractImportCommitRowDTO[] = selected.map((row) => ({
      index: row.index,
      documentName: row.documentName,
      studentId: row.studentId as string,
      contractNumber: row.contractNumber,
      contractDate: row.contractDate as string,
      amount: row.amount as number,
      currency: row.currency,
      sessionsInContract: Number(row.sessions) || 0,
      paymentType: row.paymentType,
      installmentsCount: Number(row.installments) || 1,
      intervalMonths: row.intervalMonths,
      paymentTermDays: row.paymentTermDays,
    }));
    commit({ rows: payload });
  };

  const resultOf = (index: number) => result?.rows.find((row) => row.index === index);

  return (
    <Modal isOpen={isOpen} onClose={close} size="fullScreen">
      <Modal.Header title="Import contracts from a PandaDoc export" />
      <Modal.Body className="flex flex-col gap-4">
        {step === 'pick' ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-customGrey">
              Export "Document data" from PandaDoc as CSV and upload it here. Each row becomes a
              contract: the number and student name are read from the document name, the value,
              currency, agreement date, payment frequency, term length and payment term from their
              columns. You review every row before anything is saved.
            </p>
            <label
              className={mergeClassNames(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/20 bg-surfaceObject px-6 py-10 text-center transition-colors hover:border-customPrimary',
                isPreviewing && 'pointer-events-none opacity-60'
              )}
            >
              <span className="text-sm font-semibold text-white">
                {isPreviewing ? 'Reading the file…' : 'Choose a CSV file'}
              </span>
              <span className="text-xs text-customGrey">
                {fileName ?? 'Only the PandaDoc "Document data" export is supported.'}
              </span>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => handleFile(event.target.files?.[0])}
              />
            </label>
          </div>
        ) : null}

        {step === 'review' ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="text-customGrey">
                {fileName} · {rows.length} row{rows.length === 1 ? '' : 's'} ·{' '}
                <span className="text-customGreen">{selected.length} ready</span>
                {blocked.length ? (
                  <>
                    {' · '}
                    <span className="text-customGold">{blocked.length} need attention</span>
                  </>
                ) : null}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  buttonText="Select all ready"
                  onClick={() =>
                    setRows((current) =>
                      current.map((row) => ({ ...row, include: isRowReady(row) }))
                    )
                  }
                />
                <Button
                  variant="secondary"
                  buttonText="Select none"
                  onClick={() =>
                    setRows((current) => current.map((row) => ({ ...row, include: false })))
                  }
                />
              </div>
            </div>

            <Table>
              <Table.Header>
                <Table.Row isHeader>
                  <Table.HeaderCell>Import</Table.HeaderCell>
                  <Table.HeaderCell>Document</Table.HeaderCell>
                  <Table.HeaderCell>Student</Table.HeaderCell>
                  <Table.HeaderCell>Contract</Table.HeaderCell>
                  <Table.HeaderCell>Payment</Table.HeaderCell>
                  <Table.HeaderCell>Sessions</Table.HeaderCell>
                  <Table.HeaderCell>Notes</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body hasResults={!!rows.length}>
                {rows.map((row) => {
                  const ready = isRowReady(row);
                  return (
                    <Table.Row key={row.index}>
                      <Table.Cell>
                        <input
                          type="checkbox"
                          checked={row.include}
                          disabled={!!row.existingContract}
                          onChange={(event) =>
                            updateRow(row.index, { include: event.target.checked })
                          }
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <div className="font-semibold text-white">{row.documentName}</div>
                        <div className="text-xxs text-customGrey">
                          {row.contractNumber ? `No. ${row.contractNumber}` : 'No number'}
                          {row.documentType ? ` · ${row.documentType}` : ''}
                          {row.expirationDate ? ` · expires ${formatDate(row.expirationDate)}` : ''}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-col gap-1">
                          <StudentPicker
                            row={row}
                            onPick={(id, label) =>
                              updateRow(row.index, {
                                studentId: id,
                                studentLabel: label,
                                errors: row.errors.filter((error) => !/student/i.test(error)),
                              })
                            }
                          />
                          <span className="text-xxs text-customGrey">
                            {row.match
                              ? `Matched by ${row.match.method}`
                              : row.studentName
                              ? `Name in document: ${row.studentName}`
                              : 'No name in the document'}
                            {row.counterpartyName ? ` · counterparty ${row.counterpartyName}` : ''}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div>{row.amount ? formatMoney(row.amount, row.currency) : '—'}</div>
                        <div className="text-xxs text-customGrey">
                          {row.contractDate ? formatDate(row.contractDate) : 'No date'}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div>{PAYMENT_LABELS[row.paymentType]}</div>
                        {row.paymentType === ContractPaymentType.Installments ? (
                          <div className="mt-1 flex items-center gap-2 text-xxs text-customGrey">
                            <div className="w-16">
                              <Input
                                type="number"
                                id={`installments-${row.index}`}
                                name={`installments-${row.index}`}
                                label=""
                                value={row.installments}
                                onChange={(event) =>
                                  updateRow(row.index, { installments: event.target.value })
                                }
                              />
                            </div>
                            <span>every {row.intervalMonths} mo</span>
                          </div>
                        ) : null}
                        <div className="text-xxs text-customGrey">
                          {row.paymentTermDays} day{row.paymentTermDays === 1 ? '' : 's'} payment
                          term
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="w-20">
                          <Input
                            type="number"
                            id={`sessions-${row.index}`}
                            name={`sessions-${row.index}`}
                            label=""
                            placeholder="0"
                            value={row.sessions}
                            onChange={(event) =>
                              updateRow(row.index, { sessions: event.target.value })
                            }
                          />
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {row.existingContract ? (
                          <button
                            type="button"
                            className="text-left text-customGold hover:underline"
                            onClick={() => onOpenContract(row.existingContract?._id as string)}
                          >
                            {row.existingContract.reason}
                          </button>
                        ) : null}
                        {row.errors.length ? (
                          <ul className="list-disc pl-4 text-xxs text-customRed">
                            {row.errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        ) : null}
                        {row.warnings.length ? (
                          <ul className="list-disc pl-4 text-xxs text-customGrey">
                            {row.warnings.map((warning, index) => (
                              <li key={index}>{warning}</li>
                            ))}
                          </ul>
                        ) : null}
                        {!ready && !row.errors.length && !row.existingContract ? (
                          <span className="text-xxs text-customGold">Pick a student first.</span>
                        ) : null}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        ) : null}

        {step === 'done' && result ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-white">
              {result.created} created · {result.skipped} skipped · {result.failed} failed.
              Contracts without a sessions count are marked for review.
            </p>
            <Table>
              <Table.Header>
                <Table.Row isHeader>
                  <Table.HeaderCell>Document</Table.HeaderCell>
                  <Table.HeaderCell>Result</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body hasResults={!!result.rows.length}>
                {result.rows.map((outcome) => {
                  const row = rows.find((item) => item.index === outcome.index);
                  return (
                    <Table.Row key={outcome.index}>
                      <Table.Cell>{row?.documentName ?? `Row ${outcome.index + 1}`}</Table.Cell>
                      <Table.Cell>
                        <span
                          className={mergeClassNames(
                            outcome.status === 'created' && 'text-customGreen',
                            outcome.status === 'skipped' && 'text-customGold',
                            outcome.status === 'failed' && 'text-customRed'
                          )}
                        >
                          {outcome.status}
                        </span>
                        {outcome.message ? (
                          <span className="text-customGrey"> · {outcome.message}</span>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell>
                        {outcome.contractId ? (
                          <Button
                            variant="secondary"
                            buttonText="Open contract"
                            onClick={() => onOpenContract(outcome.contractId as string)}
                          />
                        ) : null}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="mt-4 flex justify-end gap-3">
        {step === 'review' ? (
          <>
            <Button variant="secondary" buttonText="Choose another file" onClick={reset} />
            <Button
              variant="primary"
              buttonText={`Import ${selected.length} contract${selected.length === 1 ? '' : 's'}`}
              isLoading={isCommitting}
              disabled={!selected.length || isCommitting}
              onClick={submit}
            />
          </>
        ) : step === 'done' ? (
          <>
            <Button variant="secondary" buttonText="Import another file" onClick={reset} />
            <Button variant="primary" buttonText="Close" onClick={close} />
          </>
        ) : (
          <Button variant="secondary" buttonText="Cancel" onClick={close} />
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ContractImportModal;
